import React, { useEffect, useMemo, useState } from 'react';
import MetaLayoutRemainingFields from 'Components/UI/Meta/MetaForm/Components/MetaLayout/MetaLayoutRemainingFields';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import {
  FDAccordion,
  FDAccordionDetails,
  FDAccordionTitle,
  FDBodyText,
  FDHeadlineText,
  FDIcon,
  FDLozenge,
  FDPaper,
  FDStack,
} from '@facet-wealth/design';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { LockedPhase } from 'Views/Page/Content/Templates/planSummaryPageTemplate/lockedPhase';
import { dataAttrs } from 'Helpers/Testing';
import { PlanSummaryPhaseSteps } from './planSummaryPhaseSteps';

const useStyles = buildUseStyles('PlanSummaryPhase', () => ({
  rootWithAccordion: {
    '& > .FD-MuiAccordion-root > .FD-MuiAccordionSummary-root': {
      width: 'fit-content', // Achieve the design's look where the dropdown icon is at the end of the title instead of all the way on the far right of the page
      padding: 0, // Removes horizontal padding from the summary
      '& > .FD-MuiAccordionSummary-content': {
        margin: 0, // Removes vertical margin from the summary
        minHeight: 0, // Removes even more vertical spacing from the summary
      },
    },
    '& > .FD-MuiAccordion-root > * > * > * > * > .FD-MuiAccordionDetails-root': {
      padding: 0, // Removes padding from the details
    },
  },
}));

// Ensure you get an array even if the array returned from the server is null
const arrayAttr = (resource, attr) => resource?.attr(attr)?.resources()?.list() ?? [];

export const PlanSummaryPhase = () => {
  const classes = useStyles();
  const { resource, consumeAttr } = useMetaLayoutContext();

  const isCompleted = consumeAttr('isCompleted') === 'true';
  const [expanded, setExpanded] = useState(!isCompleted);
  // If isCompleted changes to true, collapse this section
  useEffect(() => {
    if (isCompleted) setExpanded(false);
  }, [isCompleted]);

  const allSteps = useMemo(() => arrayAttr(resource, 'planSteps').concat(arrayAttr(resource, 'planPhaseSummary')), [resource]);

  // Come up with the meat of the show: title and content
  const title = (
    <FDHeadlineText
      value={(
        <FDStack direction="row" alignItems="center" spacing={1}>
          {isCompleted ? <FDIcon icon="checkCircleIcon" /> : <FDIcon icon="circleOutline" />}
          {consumeAttr('title')}
          {consumeAttr('timelineSummary') ? <FDLozenge color={'info'} label={consumeAttr('timelineSummary')} /> : null}
        </FDStack>
        )}
    />
  );
  let content = (
    <FDStack direction="column" spacing={3}>
      {consumeAttr('isWaiting') === 'true'
        ? (
          <FDPaper appearance="flat">
            <FDHeadlineText size="sm" value={consumeAttr('waitingHeadline')} />
            <FDBodyText value={consumeAttr('waitingDescription')} />
          </FDPaper>
        )
        : <PlanSummaryPhaseSteps value={allSteps} />
      }
      <MetaLayoutRemainingFields />
      <MetaControlField attr="whatToExpectNextMessage" />
    </FDStack>
  );

  // Optionally hide the content if it's locked
  if (consumeAttr('isLocked') === 'true') {
    content = <LockedPhase>{content}</LockedPhase>;
  }

  // Now we can add the description as part of the content as well, and some spacing
  content = (
    <FDStack direction="column" spacing={2} paddingY={2}>
      <MetaControlField attr="description" />
      {content}
    </FDStack>
  );

  // Phases that haven't been completed are easy to render - just title + content
  if (!isCompleted) {
    return <FDStack direction="column" {...dataAttrs('incomplete-phase', resource)}>{title}{content}</FDStack>;
  }

  // Phases that have been completed need to be collapsable, so we need to render them specially
  return (
    <div className={classes.rootWithAccordion} {...dataAttrs('complete-phase', resource)}>
      <FDAccordion expanded={expanded} onChange={setExpanded}>
        <FDAccordionTitle>{title}</FDAccordionTitle>
        <FDAccordionDetails>{content}</FDAccordionDetails>
      </FDAccordion>
    </div>
  );
};
